import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { SyncTargetEmployeesGetResponse } from "@/types/swagger";
import API from "@/api";

export interface IEmployeeState {
  emp_id: number; // 従業員ID
  sync_target_group_id: number; // 同期対象グループID
  sync_histories_detail_id: number; // 同期履歴詳細ID
  tt_emp_id: number; // 従業員ID（TimeTappsから同期された情報）
  tt_company_id: number; // 会社ID（TimeTappsから同期された情報）
  company_name: string; // 会社名
  tt_department_layer_id: number | null; //  所属階層ID（TimeTappsから同期された情報）
  tt_layer: string | null; // 階層（TimeTappsから同期された情報）
  tt_layer_department_name: string | null; // 階層所属名（TimeTappsから同期された情報）
  tt_department_cd_1: string | null; // 所属CD1（TimeTappsから同期された情報）
  tt_department_name_1: string | null; // 所属名1（TimeTappsから同期された情報）
  tt_department_cd_2: string | null; // 所属CD2（TimeTappsから同期された情報）
  tt_department_name_2: string | null; // 所属名2（TimeTappsから同期された情報）
  tt_department_cd_3: string | null; // 所属CD3（TimeTappsから同期された情報）
  tt_department_name_3: string | null; // 所属名3（TimeTappsから同期された情報）
  tt_department_cd_4: string | null; // 所属CD4（TimeTappsから同期された情報）
  tt_department_name_4: string | null; // 所属名4（TimeTappsから同期された情報）
  tt_department_cd_5: string | null; // 所属CD5（TimeTappsから同期された情報）
  tt_department_name_5: string | null; // 所属名5（TimeTappsから同期された情報）
  tt_emp_cd: string; // 従業員CD（TimeTappsから同期された情報）
  tt_last_name: string; // 姓（TimeTappsから同期された情報）
  tt_first_name: string; // 名（TimeTappsから同期された情報）
  tt_last_name_kana: string; // 姓（カナ）（TimeTappsから同期された情報）
  tt_first_name_kana: string; // 名（カナ）（TimeTappsから同期された情報）
  tt_birthday: string | null; // 生年月日（TimeTappsから同期された情報）ex. 2017-07-21
  tt_gender: number | null; // 性別（TimeTappsから同期された情報）
  tt_email: string | null; // メールアドレス（TimeTappsから同期された情報）
  tt_title: string | null; // 役職（TimeTappsから同期された情報）
  tt_tel: string | null; // 電話番号（TimeTappsから同期された情報）
  tt_enter_date: string; // 入社日（TimeTappsから同期された情報） ex. 2017-07-21
  tt_account_use_start_date: string | null; //アカウント利用開始日（TimeTappsから同期された情報） ex. 2017-07-21
  tt_retire_date: string | null; // 退職日（TimeTappsから同期された情報）ex. 2017-07-21
  tt_employment_ptn_name: string; // 就業パターン名（TimeTappsから同期された情報）
  tt_employments_status: string; // 契約形態（TimeTappsから同期された情報）
  employee_id: string; // 職員ID 正規職員の場合、従業員CD 非正規職員の場合、氏名と生年月日の組み合わせを使って、システムで払い出した一意のID(数字英小文字の組み合わせ10桁)
  duplicate_flg: boolean; // 重複フラグ
  total_search_num: number; // 全検索件数
}

export interface IFetchEmployeesParameters {
  user_id: number;
  target_date: string; // YYYY-MM-DD
  emp_cd?: string;
  last_name?: string;
  first_name?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  company_id?: number;
  department_id?: number;
  offset?: number;
}

const initialState: IEmployeeState[] = [];

@Module({
  name: "Employees",
  stateFactory: true,
  namespaced: true,
})
export class Employees extends VuexModule {
  private employees: IEmployeeState[] = initialState;

  public get getEmployees() {
    return this.employees;
  }

  @Mutation set(employees: IEmployeeState[]) {
    this.employees = employees;
  }

  @Action({ rawError: true })
  public init() {
    this.set(initialState);
  }

  @Action({ rawError: true })
  public async syncEmployees(userId: number) {
    const api = new API();
    await api.post(`/users/${userId}/sync/employees`, {
      body: {},
    });
  }

  @Action({ rawError: true })
  public async fetchEmployees(params: IFetchEmployeesParameters) {
    const api = new API();

    const getEmployeesApi = async function (
      p: IFetchEmployeesParameters
    ): Promise<IEmployeeState[]> {
      const employees: IEmployeeState[] = [];
      const response: SyncTargetEmployeesGetResponse = await api.get(
        `/users/${p.user_id}/sync/employees`,
        p
      );
      if (Array.isArray(response.sync_target_employees)) {
        response.sync_target_employees.forEach((employee: any) => {
          if (employee.tt_emp_cd && employee.employee_id) {
            const emp: any = {};
            for (const [key, value] of Object.entries(employee)) {
              emp[key] = value;
            }
            employees.push(emp);
          }
        });
      }
      return employees;
    };

    let employees: IEmployeeState[] = [];
    const MAX_RETRY_COUNT = 10;
    for (let i = 0; i < MAX_RETRY_COUNT; i++) {
      const requestParams: IFetchEmployeesParameters = {
        user_id: params.user_id,
        target_date: params.target_date,
        offset: employees.length + 1,
      };
      if (params.emp_cd) requestParams.emp_cd = params.emp_cd;
      if (params.last_name) requestParams.last_name = params.last_name;
      if (params.first_name) requestParams.first_name = params.first_name;
      if (params.last_name_kana)
        requestParams.last_name_kana = params.last_name_kana;
      if (params.first_name_kana)
        requestParams.first_name_kana = params.first_name_kana;
      if (params.company_id) requestParams.company_id = params.company_id;
      if (params.department_id)
        requestParams.department_id = params.department_id;
      const res: IEmployeeState[] = await getEmployeesApi(requestParams);
      if (!Array.isArray(res) || res.length == 0) {
        break;
      } else {
        employees = employees.concat(res);
        if (res[res.length - 1].total_search_num <= employees.length) {
          break;
        }
      }
    }
    this.set(employees);
  }
}
